/* The interface catalog. Chat, home and templates all render "this came in via
   X" from it, so it lives with the other catalogs rather than inside the
   interfaces screen.

   Display only: which channels exist, how they look, where they sort. How one
   is connected is its setup flow in `features/interfaces`, and what an agent
   has connected comes from `/channels` — a channel without a setup flow is
   shown as coming soon. */

import { Phone, Globe, Braces, Mail, Chrome } from "lucide-react";

import type { InterfaceDef, RunInterface, SystemInterface } from "@/shared/types";

/* An interface is a door end-users walk through to reach the agent — the
   inverse of Apps (tools the agent reaches out to). */
export const INTERFACE_CATALOG: InterfaceDef[] = [
  // ---- messaging -----------------------------------------------------------
  {
    type: "whatsapp", name: "WhatsApp", brand: "whatsapp", category: "Messaging",
    desc: "Chat with the agent from any phone. Replies arrive as ordinary WhatsApp messages.",
  },
  {
    type: "slack", name: "Slack", brand: "slack", category: "Messaging",
    desc: "Mention the agent in a channel or DM it. Threads map to chats automatically.",
  },
  {
    type: "teams", name: "Microsoft Teams", brand: "teams", category: "Messaging",
    desc: "Add the agent as a Teams app. It answers 1:1 chats, @mentions and the channels you pick.",
  },
  {
    type: "discord", name: "Discord", brand: "discord", category: "Messaging",
    desc: "Invite the agent as a bot. It answers DMs, @mentions and the channels you pick in your server.",
  },
  {
    type: "telegram", name: "Telegram", brand: "telegram", category: "Messaging",
    desc: "A Telegram bot with the agent behind it — works in groups and private chats.",
  },
  {
    type: "email", name: "Email", Icon: Mail, category: "Messaging",
    desc: "Every agent gets its own address. Forward or write to it — replies land in the thread.",
  },
  // ---- voice ---------------------------------------------------------------
  {
    type: "voice", name: "Phone call", Icon: Phone, category: "Voice",
    desc: "A real phone number. Callers talk to the agent — it speaks with a natural voice.",
  },
  // ---- custom --------------------------------------------------------------
  {
    type: "web", name: "Client app", Icon: Globe, category: "Custom",
    desc: "The agent's own page. People sign in, chat, download what it made for them and connect their own accounts.",
  },
  {
    type: "extension", name: "Chrome extension", Icon: Chrome, category: "Custom",
    desc: "People chat with the agent in Chrome's side panel, and it can see and act on the sites they allow.",
  },
  {
    type: "api", name: "REST API", Icon: Braces, category: "Custom",
    desc: "Build your own interface — send messages over HTTPS, receive replies and events via webhook.",
  },
];

export const catalogFor = (type: RunInterface): InterfaceDef | undefined => INTERFACE_CATALOG.find((m) => m.type === type);

/** The agent's own interfaces, which every agent has and nobody connects. */
const SYSTEM_NAMES: Record<SystemInterface, string> = {
  platform: "Platform",
  api: "API",
  schedule: "Schedule",
  app_trigger: "App trigger",
  eval: "Eval",
};

/** What a run's interface is called: "Platform", "Schedule", "WhatsApp"… */
export const interfaceName = (type: RunInterface): string =>
  SYSTEM_NAMES[type as SystemInterface] ?? catalogFor(type)?.name ?? type;
